/**
 * The workspace floating chat surface: swaps between the FAB launcher (closed)
 * and the floating/resizable chat window (open), rendered inside the layout's
 * content region so it sits clear of the left rail and right pane. Route- and
 * device-gated (see `useIsFloatingChatRouteActive`) — it renders nothing on
 * canvas, the full chat page, or mobile; the provider retains state so returning
 * restores the window.
 *
 * Full-height mode makes room to its side rather than overlapping: normally it
 * narrows <main> (`dockReservedWidthPx`). While the right pane is expanded
 * full-width the chat moves to the LEFT of it instead of hiding (canvas
 * parity): the FAB and the windowed float pin fixed over the pane, and
 * full-height docks as a resizable column the pane shrinks beside
 * (`FloatingChatLeftDock` reserves `leftChatDockPx` from its live width) — a
 * true two-pane view, e.g. reading an expanded file preview while chatting.
 *
 * The workspace twin of `CanvasFloatingChatPanel`: both render the shared
 * `FloatingChatSurface` (see `components/FloatingChat/`), this one minus the
 * canvas-only toolbar-clearance math, plus the `<main>`-narrowing dock reserve.
 */

import {
  useRecentWorkspaceFlowSessions,
  useWorkspaceFlowSessions,
} from "@/api/flow_sessions";
import { usePrivateWorkspace } from "@/api/workspaces";
import {
  FLOATING_CHAT_MIN_HEIGHT_PX,
  FLOATING_CHAT_REGION_VERTICAL_INSET_PX,
} from "@/components/FloatingChat/floatingChatShell";
import { FloatingChatSurface } from "@/components/FloatingChat/FloatingChatSurface";
import { useAnchorRect } from "@/components/FloatingChat/useAnchorRect";
import { useRegionMaxPanelHeight } from "@/components/FloatingChat/useRegionMaxPanelHeight";
import { useViewportSize } from "@/components/FloatingChat/useViewportSize";
import { ROUTES } from "@/constants/routes";
import { useRightPaneExpandContext } from "@/context/RightPaneExpandContext";
import {
  useIsFloatingChatRouteActive,
  useWorkspaceFloatingChat,
  useWorkspaceFloatingChatLayout,
} from "@/context/WorkspaceFloatingChatContext";
import { useComposerProgress } from "@/hooks/useComposerProgress";
import { useWorkspaceLeftRailLayoutWidth } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/WorkspaceLeftRail";
import { HOME_BROWSER_LIMIT } from "@/pages/workspaces/home/utils/homeBrowserTypes";
import * as analytics from "@/utils/analytics";
import { route } from "@/utils/route";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useMatch, useNavigate } from "react-router-dom";
import { WorkspaceChatFab } from "./WorkspaceChatFab";
import { WorkspaceFloatingChatPane } from "./WorkspaceFloatingChatPane";

/**
 * Content the full-height dock must always leave to the left of itself. The dock
 * pushes <main> narrower (it doesn't overlap), so its max width is capped at
 * `regionWidth - this` to keep the center column readable when docked.
 */
const MIN_DOCKED_CONTENT_WIDTH_PX = 560;

export function WorkspaceFloatingChatPanel() {
  const routeActive = useIsFloatingChatRouteActive();
  const navigate = useNavigate();
  const {
    currentSessionId,
    canGoBack,
    goBack,
    openSession,
    isOpen,
    sizeClass,
    toggleSizeClass,
    collapse,
    startDraft,
    expand,
  } = useWorkspaceFloatingChat();
  const { setDockReservedWidthPx } = useWorkspaceFloatingChatLayout();
  const { isExpanded, rightPanelWidth } = useRightPaneExpandContext();

  const isFullHeight = sizeClass === "full-height";
  const rightPaneExpandedFullWidth = isExpanded && rightPanelWidth > 0;
  // While the detail pane is expanded full-width it occupies the right, so the
  // chat lives on the LEFT (canvas parity): FAB and windowed float fixed over
  // the pane; full-height docks as a resizable column the pane shrinks beside
  // (`FloatingChatLeftDock` reserves `leftChatDockPx` itself). routeActive is
  // redundant behind the render gate but kept so the flag stays safe to lift
  // into an effect.
  const dockLeft = routeActive && rightPaneExpandedFullWidth;

  // ── Scope resolution (route-derived) ──────────────────────────────────────
  const workspaceMatch = useMatch(ROUTES.WORKSPACES.DETAIL);
  const routeWorkspaceId = workspaceMatch?.params?.workspaceId;
  const privateWorkspace = usePrivateWorkspace();
  const scopeWorkspaceId = routeWorkspaceId ?? privateWorkspace?.id;

  const { data: workspaceSessions = [] } = useWorkspaceFlowSessions(
    routeWorkspaceId,
    { enabled: routeActive && !!routeWorkspaceId },
  );
  const { data: recentSessions = [] } = useRecentWorkspaceFlowSessions(
    HOME_BROWSER_LIMIT,
    { enabled: routeActive && !routeWorkspaceId },
  );

  const sessions = useMemo(() => {
    const list = routeWorkspaceId
      ? // Guard stale rows the same way HomeBrowser does — the cache can hold
        // a row whose session moved workspaces before the list resettled.
        workspaceSessions.filter(
          (s) => s.flow_session?.workspace_id === routeWorkspaceId,
        )
      : recentSessions;
    return list.filter((s) => !s.is_archived);
  }, [routeWorkspaceId, workspaceSessions, recentSessions]);

  const mostRecentInScopeId = sessions[0]?.flow_session_id ?? null;

  // Pulse the FAB while the active (or, when minimized to FAB, most-recent)
  // session is running — background sessions show nothing (canvas parity).
  const progress = useComposerProgress(currentSessionId ?? mostRecentInScopeId);

  // ── Window geometry — measure the content region via an always-mounted
  // sentinel (the region itself is our positioned parent). The rect's
  // left/top/height stay valid even when a full-width detail pane squeezes the
  // region to ~0 width — the workspace twin of the canvas panel's
  // reactFlowWrapperEl anchor. ────────────────────────────────────────────────
  const shellRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  // Rail collapse shifts the region left WITHOUT resizing it while the pane is
  // expanded (the region width is pinned to the dock width) — a ResizeObserver
  // can't see position-only shifts, so re-measure on rail width changes.
  const leftRailWidth = useWorkspaceLeftRailLayoutWidth();
  const anchorRect = useAnchorRect(anchorEl, leftRailWidth);
  const { viewportWidthPx, viewportHeightPx } = useViewportSize();
  const maxHeightPx = useRegionMaxPanelHeight(anchorEl);
  const regionWidthPx = anchorRect?.width ?? 760;

  const initialHeight = Math.max(
    FLOATING_CHAT_MIN_HEIGHT_PX,
    Math.min(560, maxHeightPx),
  );

  const maxWidthPx = Math.max(
    400,
    regionWidthPx - FLOATING_CHAT_REGION_VERTICAL_INSET_PX,
  );
  // Docked, the panel makes room to its side (narrowing <main>), so cap it
  // short of the region to always leave a usable column.
  const dockMaxWidthPx = Math.max(
    400,
    maxWidthPx - MIN_DOCKED_CONTENT_WIDTH_PX,
  );

  // Windowed chat floating over a full-width detail: keep it from covering the
  // whole pane.
  const leftWindowMaxWidthPx = Math.max(400, Math.floor(viewportWidthPx * 0.5));

  // Report the normal full-height dock's live width so the layout narrows
  // <main> beside it instead of being overlapped. (The dockLeft column reserves
  // `leftChatDockPx` itself — see FloatingChatLeftDock.) Reset whenever the
  // normal dock isn't showing.
  const isDockedNormal =
    routeActive && isOpen && isFullHeight && !rightPaneExpandedFullWidth;
  useLayoutEffect(() => {
    const el = shellRef.current;
    if (!isDockedNormal || !el) {
      setDockReservedWidthPx(0);
      return;
    }
    // offsetWidth is the layout width — unaffected by the enter animation's
    // scale transform (getBoundingClientRect would read the scaled width).
    const measure = () => setDockReservedWidthPx(el.offsetWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => {
      ro.disconnect();
      setDockReservedWidthPx(0);
    };
  }, [isDockedNormal, setDockReservedWidthPx]);

  const handleFabOpen = () => {
    expand(mostRecentInScopeId ?? undefined);
    analytics.trackFloatingChatOpened({
      source: "fab",
      workspaceScoped: !!routeWorkspaceId,
      sizeClass,
    });
  };

  const handleMinimize = () => {
    analytics.trackFloatingChatMinimized();
    collapse();
  };

  const handleOpenAsTab = () => {
    if (!currentSessionId) return;
    navigate(route(ROUTES.CHAT.DETAIL, { flowSessionId: currentSessionId }));
    analytics.trackFloatingChatOpenedAsTab();
    collapse();
  };

  const paneProps = {
    flowSessionId: currentSessionId,
    scopeWorkspaceId,
    sessions,
    canGoBack,
    onGoBack: goBack,
    onClose: handleMinimize,
    onToggleExpand: toggleSizeClass,
    onOpenSession: openSession,
    onNewChat: startDraft,
    onOpenAsTab: handleOpenAsTab,
  };

  // ── Render (all hooks above) ──────────────────────────────────────────────
  if (!routeActive) return null;

  return (
    <>
      {/* Geometry sentinel: mirrors the content region's box so its rect stays
          measurable (left/top/height) even when a full-width pane squeezes the
          region to ~0 width. Always mounted so the ResizeObserver never
          detaches across surface-branch switches. */}
      <div
        ref={setAnchorEl}
        aria-hidden
        className="pointer-events-none absolute inset-0"
      />
      <FloatingChatSurface
        isOpen={isOpen}
        dockLeft={dockLeft}
        sizeClass={sizeClass}
        anchorRect={anchorRect}
        viewportHeightPx={viewportHeightPx}
        storageKey="workspace-floating-chat-width"
        shellRef={shellRef}
        windowMaxWidthPx={maxWidthPx}
        dockedMaxWidthPx={dockMaxWidthPx}
        initialHeightPx={initialHeight}
        minHeightPx={FLOATING_CHAT_MIN_HEIGHT_PX}
        maxHeightPx={maxHeightPx}
        leftWindowMaxWidthPx={leftWindowMaxWidthPx}
        renderPane={({ isChatExpanded }) => (
          <WorkspaceFloatingChatPane
            {...paneProps}
            isChatExpanded={isChatExpanded}
          />
        )}
        renderFab={(dockStyle) => (
          <WorkspaceChatFab
            onOpen={handleFabOpen}
            running={progress.isRunning}
            dockStyle={dockStyle}
          />
        )}
      />
    </>
  );
}
